export type SavedContentType = "places" | "guides"

export interface SavedContentSyncMessage {
  type: "CONTENT_CHANGED"
  contentType: SavedContentType
}

export class SavedContentSync {
  private static _instance: SavedContentSync | undefined
  private channel: BroadcastChannel | undefined
  private listeners: ((message: SavedContentSyncMessage) => void)[] = []

  static get instance(): SavedContentSync {
    if (!SavedContentSync._instance) {
      SavedContentSync._instance = new SavedContentSync()
    }
    return SavedContentSync._instance
  }

  private constructor() {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
      return
    }

    try {
      this.channel = new BroadcastChannel("mindtrip-saved-content")
    } catch (error) {
      // SecurityError marks denied storage access; NotSupportedError marks an
      // opaque origin in Chromium-based clients. Cross-tab sync is optional, so
      // both use the existing no-channel fallback.
      if (
        error instanceof DOMException &&
        (error.name === "SecurityError" || error.name === "NotSupportedError")
      ) {
        return
      }

      throw error
    }

    this.channel.addEventListener("message", this.handleMessage.bind(this))
  }

  private handleMessage(event: MessageEvent<SavedContentSyncMessage>) {
    this.listeners.forEach((listener) => {
      try {
        listener(event.data)
      } catch (error) {
        console.error("Error in SavedContentSync listener:", error)
      }
    })
  }

  // broadcast that content has changed (saved, unsaved, or list updated)
  broadcastContentChanged(contentType: SavedContentType) {
    if (!this.channel) {
      return
    }

    const message: SavedContentSyncMessage = {
      type: "CONTENT_CHANGED",
      contentType,
    }

    this.channel.postMessage(message)
  }

  addListener(listener: (message: SavedContentSyncMessage) => void) {
    this.listeners.push(listener)
  }

  removeListener(listener: (message: SavedContentSyncMessage) => void) {
    const index = this.listeners.indexOf(listener)
    if (index > -1) {
      this.listeners.splice(index, 1)
    }
  }

  destroy() {
    if (this.channel) {
      this.channel.close()
      this.channel = undefined
    }
    this.listeners = []
  }
}
